import { Navigate, Route, Routes } from 'react-router';
import { SURFACE_URLS, publicApi, type ApiReference } from '@p1plug/platform';
import { SidebarShell } from '@p1plug/ui';
import { gateways } from './gateway';
import { LanguageProvider } from './doc';
import { Home } from './pages/Home';
import { Quickstart } from './pages/Quickstart';
import { Concepts, Environments, Changelog } from './pages/Concepts';
import { Authentication } from './pages/Authentication';
import { Webhooks } from './pages/Webhooks';
import { Errors } from './pages/Errors';
import { Sandbox } from './pages/Sandbox';
import { Reference, ReferenceGroup, groupSlug } from './pages/Reference';
import { AccountCredentials, AccountUsage, AccountWebhooks } from './pages/Account';

const api = publicApi as ApiReference;
const groups = [...new Set(api.operations.map((o) => o.group))];

export function App() {
  return (
    <LanguageProvider>
      <SidebarShell
        product="Developers"
        demo={gateways.mode === 'demo'}
        demoText="Demo build. The documentation is the real API contract; the pages under Your account show sample data."
        nav={[
          {
            items: [
              { label: 'Overview', to: '/', end: true },
              { label: 'Quickstart', to: '/docs/quickstart' },
              { label: 'Concepts', to: '/docs/concepts' },
            ],
          },
          {
            title: 'Guides',
            items: [
              { label: 'Authentication', to: '/docs/authentication' },
              { label: 'Webhooks and events', to: '/docs/webhooks' },
              { label: 'Errors', to: '/docs/errors' },
              { label: 'Environments', to: '/docs/environments' },
              { label: 'Sandbox', to: '/docs/sandbox' },
            ],
          },
          {
            title: 'API reference',
            items: [
              { label: 'All operations', to: '/reference', end: true },
              ...groups.map((g) => ({ label: g, to: `/reference/${groupSlug(g)}` })),
              { label: 'Changelog', to: '/docs/changelog' },
            ],
          },
          {
            title: 'Your account',
            items: [
              { label: 'Applications and credentials', to: '/account/credentials' },
              { label: 'Webhook deliveries', to: '/account/webhooks' },
              { label: 'API usage', to: '/account/usage' },
            ],
          },
        ]}
        meta={
          <>
            <span>API version {api.version}</span>
            <span>
              <a href={SURFACE_URLS.status}>Service status</a>
            </span>
          </>
        }
      >
        <Routes>
          <Route index element={<Home />} />
          <Route path="docs/quickstart" element={<Quickstart />} />
          <Route path="docs/concepts" element={<Concepts />} />
          <Route path="docs/authentication" element={<Authentication />} />
          <Route path="docs/webhooks" element={<Webhooks />} />
          <Route path="docs/errors" element={<Errors />} />
          <Route path="docs/environments" element={<Environments />} />
          <Route path="docs/sandbox" element={<Sandbox />} />
          <Route path="docs/changelog" element={<Changelog />} />
          <Route path="reference" element={<Reference />} />
          <Route path="reference/:group" element={<ReferenceGroup />} />
          <Route path="account/credentials" element={<AccountCredentials />} />
          <Route path="account/webhooks" element={<AccountWebhooks />} />
          <Route path="account/usage" element={<AccountUsage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SidebarShell>
    </LanguageProvider>
  );
}
